import type { Me, ModulesResponse } from '@portal/contracts';
import { useEffect, useState } from 'react';
import { api, HttpError } from './api.js';
import { CrestBackground } from './decoration/CrestBackground.js';
import { resolveLocation } from './navigation.js';
import { Arbeitsplatz } from './pages/Arbeitsplatz.js';
import { Crm } from './pages/crm/Crm.js';
import { ModulePage, Problem } from './pages/ModulePage.js';
import { Verwaltung } from './pages/verwaltung/Verwaltung.js';
import { usePathname } from './router.js';
import { Header, SubNav } from './shell/Header.js';
import { applyTheme, initialTheme, type Theme } from './theme.js';

type Boot = { kind: 'loading' } | { kind: 'login' } | { kind: 'error'; status: number } | { kind: 'ready'; me: Me; nav: ModulesResponse };

export function App() {
  const [boot, setBoot] = useState<Boot>({ kind: 'loading' });
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const pathname = usePathname();

  useEffect(() => applyTheme(theme), [theme]);

  // #819/#820 Fallback zur 301-Umleitung im Caddyfile: alte Routen /kunden/crm… → /crm… und /system/verwaltung… → /verwaltung… clientseitig umschreiben
  useEffect(() => {
    const { pathname: p, search, hash } = window.location;
    const neu = p.replace(/^\/kunden\/crm(?=\/|$)/, '/crm').replace(/^\/system\/verwaltung(?=\/|$)/, '/verwaltung');
    if (neu === p) return;
    window.history.replaceState(null, '', neu + search + hash);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  useEffect(() => {
    Promise.all([api.me(), api.modules()]).then(
      ([me, nav]) => setBoot({ kind: 'ready', me, nav }),
      (err: unknown) => {
        const status = err instanceof HttpError ? err.status : 0;
        setBoot(status === 401 ? { kind: 'login' } : { kind: 'error', status });
      },
    );
  }, []);

  if (boot.kind === 'loading') return <p className="sh-status">Wird geladen …</p>;
  if (boot.kind === 'login') return <Login />;
  if (boot.kind === 'error')
    return (
      <main className="sh-main">
        <Problem status={boot.status} />
      </main>
    );

  const { me, nav } = boot;
  const loc = resolveLocation(nav.areas, pathname);
  return (
    <div className="sh">
      <Header me={me} areas={nav.areas} activeArea={loc.area?.key ?? null} theme={theme} onToggleTheme={() => setTheme(theme === 'light' ? 'dark' : 'light')} />
      {loc.area && <SubNav area={loc.area} activeModule={loc.module?.key ?? null} />}
      <main className="sh-main">
        {!loc.module ? (
          <Problem status={pathname === '/' ? 403 : 404} />
        ) : loc.module.key === 'arbeitsplatz' ? (
          <Arbeitsplatz me={me} />
        ) : loc.module.key === 'verwaltung' ? (
          <Verwaltung me={me} pathname={pathname} />
        ) : loc.module.key === 'crm' ? (
          <Crm me={me} pathname={pathname} />
        ) : (
          <ModulePage moduleKey={loc.module.key} />
        )}
      </main>
    </div>
  );
}

function Login() {
  const params = new URLSearchParams(window.location.search);
  const abgemeldet = params.get('abgemeldet') === '1';
  // #807: Hinweis-Parameter nicht in returnTo übernehmen (sonst nach dem nächsten Login wieder /?abgemeldet=1)
  params.delete('abgemeldet');
  const rest = params.toString();
  const returnTo = window.location.pathname + (rest ? `?${rest}` : '');
  return (
    <main className="sh-login">
      <CrestBackground />
      <div className="pb-card sh-login__card">
        <span className="sh-login__logo">
          <img src="/brand/logo-braun.png" alt="Parfümerie Brückner" width={480} height={203} />
        </span>
        {/* Begrüßungsbereich (Controlling 03.11., ersetzt #814 „Team“): echte Überschrift + Texte, linksbündig */}
        <div className="sh-login__gruss">
          <p className="sh-login__kicker">Dein Arbeitsplatz</p>
          <h1>Willkommen zurück.</h1>
          <p className="sh-login__unterzeile">Ein guter Tag beginnt hier.</p>
        </div>
        {abgemeldet && (
          <p className="sh-login__status" role="status">
            Sie wurden abgemeldet.
          </p>
        )}
        <a className="pb-button" href={api.loginUrl(returnTo)}>
          Anmelden
        </a>
      </div>
    </main>
  );
}
